// VerifyEmailModal - Shown when user tries to convert before verifying email
import { useState, useEffect, useRef } from "react";
import { Mail, X, RefreshCw, AlertCircle } from "lucide-react";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../../firebase/firebase";
import classes from "./NoCreditsModal.module.css";

const RESEND_COOLDOWN_SECONDS = 60;

export default function VerifyEmailModal({ isOpen, onClose, email = "" }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSent(false);
      setError("");
      setInfo("");
      setCooldown(0);
      setChecking(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setError("");
    setInfo("");
    setSending(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("יש להתחבר מחדש כדי לשלוח מייל אימות.");
        return;
      }
      await sendEmailVerification(user);
      setSent(true);
      startCooldown();
    } catch (e) {
      console.error("resend verification failed:", e);
      const code = e?.code || "";
      if (code === "auth/too-many-requests") {
        setError("נשלחו יותר מדי מיילים בזמן קצר. נסה שוב בעוד מספר דקות.");
      } else {
        setError("שליחת המייל נכשלה. נסה שוב מאוחר יותר.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleICheckedItClicked = async () => {
    if (checking) return;
    setError("");
    setInfo("");
    setChecking(true);
    try {
      const user = auth.currentUser;
      if (user) await user.reload();
      const refreshed = auth.currentUser;
      if (refreshed?.emailVerified) {
        onClose();
        return;
      }
      setInfo("עדיין לא זיהינו אימות. ודא שלחצת על הקישור במייל ונסה שוב בעוד רגע.");
    } catch (e) {
      console.error("verify reload failed:", e);
      setError("לא הצלחנו לבדוק את סטטוס האימות. נסה שוב מאוחר יותר.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className={classes.overlay}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <button className={classes.closeButton} onClick={onClose}>
          <X size={24} />
        </button>

        <div className={classes.iconWrapper}>
          <div
            className={classes.iconCircle}
            style={{ background: "#FEF3C7" }}
          >
            <Mail size={48} className={classes.icon} style={{ color: "#B45309" }} />
          </div>
        </div>

        <h2 className={classes.title}>צריך לאמת את המייל</h2>

        <p className={classes.message}>
          כדי להמיר נסחי טאבו לאקסל יש לאמת את כתובת המייל שלך.
          {email ? (
            <>
              <br />
              שלחנו לך מייל אימות לכתובת <strong>{email}</strong>.
            </>
          ) : (
            <>
              <br />
              שלחנו לך מייל אימות בעת ההרשמה.
            </>
          )}
        </p>

        <div
          style={{
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            borderRadius: 12,
            padding: "10px 14px",
            margin: "10px 0 14px",
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            textAlign: "right",
            direction: "rtl",
            fontSize: 13,
            color: "#78350F",
            lineHeight: 1.5,
          }}
        >
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>לא מוצא את המייל?</strong>
            <br />
            בדוק בתיקיית <strong>הספאם / קידומי מכירות / דואר זבל</strong>.
            לפעמים מיילי האימות נופלים לשם.
          </div>
        </div>

        {sent && !error && (
          <div
            style={{
              background: "#D1FAE5",
              border: "1px solid #10B981",
              borderRadius: 12,
              padding: "10px 14px",
              margin: "0 0 12px",
              fontSize: 13,
              color: "#065F46",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            מייל אימות חדש נשלח! בדוק את תיבת הדואר (וגם את הספאם).
          </div>
        )}

        {info && !error && (
          <div
            style={{
              background: "#FEF3C7",
              border: "1px solid #F59E0B",
              borderRadius: 12,
              padding: "10px 14px",
              margin: "0 0 12px",
              fontSize: 13,
              color: "#78350F",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            {info}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "#FEE2E2",
              border: "1px solid #EF4444",
              borderRadius: 12,
              padding: "10px 14px",
              margin: "0 0 12px",
              fontSize: 13,
              color: "#991B1B",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            {error}
          </div>
        )}

        <button
          className={classes.buyButton}
          onClick={handleResend}
          disabled={sending || cooldown > 0}
          style={cooldown > 0 ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
        >
          <RefreshCw size={20} />
          {sending
            ? "שולח..."
            : cooldown > 0
            ? `אפשר לשלוח שוב בעוד ${cooldown} שניות`
            : "שלח לי שוב את המייל"}
        </button>

        <button
          className={classes.laterButton}
          onClick={handleICheckedItClicked}
          disabled={checking}
        >
          {checking ? "בודק..." : "כבר אימתתי – בדוק עכשיו"}
        </button>
      </div>
    </div>
  );
}
